import React, { useState } from "react";
import {
  Card,
  Space as AntdSpace,
  Button,
  Table,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Progress,
  Input,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import { ColumnsType } from "antd/es/table";
import {
  useReportsServiceDownloadLowStockAlertReportV2,
  useReportsServiceGetLowStockAlertReportV2,
} from "api/queries";

const { Title } = Typography;

const LowStockReport = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");

  const offset = (page - 1) * limit;

  const { data, isLoading } = useReportsServiceGetLowStockAlertReportV2({
    limit,
    offset,
    search,
  });

  const { mutate: downloadReport, isPending: isDownloadPending } =
    useReportsServiceDownloadLowStockAlertReportV2();

  const columns: ColumnsType<any> = [
    {
      title: "Product",
      dataIndex: "productName",
      key: "productName",
    },
    {
      title: "SKU",
      dataIndex: "sku",
      key: "sku",
    },
    {
      title: "Category",
      dataIndex: "categoryName",
      key: "categoryName",
    },
    {
      title: "Subcategory",
      dataIndex: "subCategoryName",
      key: "subCategoryName",
    },
    {
      title: "Brand",
      dataIndex: "brandName",
      key: "brandName",
    },
    {
      title: "Current Stock",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Stock Level",
      dataIndex: "stockLevelPercentage",
      key: "stockLevelPercentage",
      render: (stockLevel: number) => (
        <Progress
          percent={stockLevel}
          size="small"
          status={stockLevel < 30 ? "exception" : "active"}
          strokeColor={stockLevel < 50 ? "#faad14" : "#52c41a"}
        />
      ),
    },
    {
      title: "Minimum Required",
      dataIndex: "minimumRequired",
      key: "minimumRequired",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        const color = status === "CRITICAL" ? "red" : "orange";
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "Restock Cost",
      dataIndex: "restockCost",
      key: "restockCost",
      render: (cost: number) => (cost ? cost.toLocaleString() : "-"),
    },
  ];

  const handleTableChange = (pagination: any) => {
    if (pagination && pagination.current && pagination.pageSize) {
      setPage(pagination.current || 1);
      setLimit(pagination.pageSize || 10);
    }
  };

  const handleDownload = () => {
    downloadReport(
      {
        search,
      },
      {
        onSuccess: (response: any) => {
          if (response?.downloadUrl) {
            window.open(response.downloadUrl, "_blank");
          }
        },
      }
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5, delay: 0 }}
    >
      <AntdSpace direction="vertical" size="large" style={{ width: "100%" }}>
        <AntdSpace style={{ width: "100%", justifyContent: "space-between" }}>
          <Title level={2}>Low Stock Alert Report</Title>
          <AntdSpace>
            <Input.Search
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search"
              style={{ width: 200 }}
            />
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={isDownloadPending}
              onClick={handleDownload}
            >
              Download Report
            </Button>
          </AntdSpace>
        </AntdSpace>
        {/* Summary Statistics */}
        <Row gutter={16}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Low Stock Items"
                value={data?.kpis?.totalLowStock}
                loading={isLoading}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Critical Items"
                value={data?.kpis?.totalCritical}
                loading={isLoading}
                valueStyle={{ color: "#cf1322" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Warning Items"
                value={data?.kpis?.totalWarning}
                loading={isLoading}
                valueStyle={{ color: "#faad14" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Est. Restock Cost"
                value={data?.kpis?.estimatedRestockCost}
                precision={2}
                loading={isLoading}
              />
            </Card>
          </Col>
        </Row>
        {/* Main Data Table */}
        <Table
          columns={columns}
          dataSource={data?.items}
          pagination={{
            current:
              data?.pagination?.offset !== undefined
                ? Math.floor(data.pagination.offset / data.pagination.limit) + 1
                : page,
            pageSize: data?.pagination?.limit || limit,
            total: data?.pagination?.total || 0,
            onChange: (page, pageSize) => {
              setPage(page);
              setLimit(pageSize);
            },
            pageSizeOptions: [10, 20, 50, 100],
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          loading={isLoading}
          scroll={{ x: "max-content" }}
          rowKey={(item) => item.productId}
          onChange={handleTableChange}
        />
      </AntdSpace>
    </motion.div>
  );
};

export default LowStockReport;
